import React, { useState, useMemo } from 'react'
import {
  Box, Button, MenuItem, Select, Table, TableBody, TableCell,
  TableRow, TextField,
} from '@material-ui/core'
import Search from '../../../../elements/icons/Search'
import Download from '../../../../elements/icons/Download'
import { DisplayMessage, Loader } from '../../../../Components'
import { eventService } from '../../../../Services/EventService'
import { connect } from 'react-redux'
import * as messageTypes from '../../../../utils/messageTypes'
import { getErrorMessage, SortOrder } from '../constants'
import TableData from '../TableData'
import { useStyles } from './styles'
import {
  CSV_FILE_PREFIX, CSV_HEADERS, DEFAULT_MONTH, EMPTY_TABLE_TEXT,
  FETCH_ERROR_MESSAGE, INVOICE_TABLE_COLUMNS, MONTHS,
} from './constants'

const fmtMonth = k => {
  if (!k || k.length !== 6) return k ?? ''
  return MONTHS[parseInt(k.slice(4, 6), 10) - 1] + " '" + k.slice(2, 4)
}

const fmtNum = n => {
  if (n == null) return '—'
  if (n === 0) return '0'
  if (n >= 1e9) return (n / 1e9).toFixed(2).replace(/\.?0+$/, '') + 'B'
  if (n >= 1e6) return (n / 1e6).toFixed(2).replace(/\.?0+$/, '') + 'M'
  if (n >= 1e3) return (n / 1e3).toFixed(1).replace(/\.?0+$/, '') + 'K'
  return n.toLocaleString()
}

const fmtAmt = (val, cur) => {
  if (val == null) return '—'
  const sym = cur === 'EUR' ? '€' : '$'
  return sym + (Math.round(val * 100) / 100).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
}

const fmtProgName = p => String(p ?? '').replace(/_/g, ' ')

const escapeCsvValue = v => {
  const s = v == null ? '' : String(v)
  return (s.includes(',') || s.includes('"') || s.includes('\n')) ? `"${s.replace(/"/g, '""')}"` : s
}

const getSortValue = (r, col) => {
  const map = {
    account: r.accountName ?? '', program: r.programName ?? '',
    month: r.monthKey ?? '', currency: r.config?.billingCurrency ?? '',
    totalHits: r.totalApiHits ?? 0, freeHits: r.freeHitsAllotted ?? 0,
    overageHits: r.overageApiHits ?? 0, apiOvgFees: r.totalApiOverageFees ?? 0,
    activeSites: r.activeSitesCount ?? 0, billableSites: r.billableSitesCount ?? 0,
    sitesFees: r.totalActiveSitesFees ?? 0, hrtSites: r.hrtSitesCount ?? 0,
    hrtFees: r.totalHrtSitesFees ?? 0, fixedFees: r.fixedFees ?? 0,
    totalAmount: r.totalAmount ?? 0, taxAmount: r.taxAmount ?? 0,
    totalWithTax: r.totalAmountWithTax ?? 0,
  }
  return map[col] ?? 0
}

const ConfigPanel = ({ r, classes }) => {
  const cfg = r.config ?? {}
  const cur = cfg.billingCurrency ?? 'USD'
  const sym = cur === 'EUR' ? '€' : '$'
  const bool = v => (
    <Box component="span" className={v ? classes.boolYes : classes.boolNo}>{v ? 'YES' : 'NO'}</Box>
  )
  const cfgRow = (label, val) => (
    <TableRow key={label}>
      <TableCell>{label}</TableCell>
      <TableCell>{val ?? '—'}</TableCell>
    </TableRow>
  )
  return (
    <Box className={classes.cfgPanel}>
      <Box className={classes.cfgMeta}>
        Billing config for <strong>{r.accountName}</strong>
        <Box component="span" className={classes.cfgId}>{cfg.id ?? ''}</Box>
      </Box>
      <Box className={classes.cfgGrid}>
        <Box className={classes.cfgSection}>
          <Box className={classes.cfgSectionTitle}>Billing</Box>
          <Table size="small" className={classes.cfgTable}>
            <TableBody>
              {cfgRow('Billable', bool(cfg.isBillable))}
              {cfgRow('Type', cfg.billingType)}
              {cfgRow('Currency', cur)}
              {cfgRow('Fixed Monthly', `${sym}${(cfg.fixedMonthlyFees ?? 0).toFixed(2)}`)}
              {cfgRow('Fixed Yearly', `${sym}${(cfg.fixedYearlyFees ?? 0).toFixed(2)}`)}
              {cfg.vatPercent != null && cfgRow('VAT %', `${cfg.vatPercent}%`)}
            </TableBody>
          </Table>
        </Box>
        <Box className={classes.cfgSection}>
          <Box className={classes.cfgSectionTitle}>API Hits</Box>
          <Table size="small" className={classes.cfgTable}>
            <TableBody>
              {cfgRow('Billable', bool(cfg.apiHitsBillable))}
              {cfgRow('Free Hits / Site', (cfg.monthlyFreeHitsPerSite ?? 0).toLocaleString())}
              {cfg.slabHitsEnabled
                ? <React.Fragment key="slab">
                    {cfgRow('Slab Size', (cfg.slabSize ?? 0).toLocaleString())}
                    {cfgRow('Average / Slab', `${sym}${cfg.overageFeesPerSlab ?? 0}`)}
                  </React.Fragment>
                : cfgRow('Average / Hit', `${sym}${cfg.overageFeesPerHit ?? 0}`)
              }
            </TableBody>
          </Table>
        </Box>
        <Box className={classes.cfgSection}>
          <Box className={classes.cfgSectionTitle}>Active Sites</Box>
          <Table size="small" className={classes.cfgTable}>
            <TableBody>
              {cfgRow('Billable', bool(cfg.activeSitesBillable))}
              {cfgRow('Billable Type', cfg.activeSitesBillableType)}
              {cfgRow('Fee / Site', `${sym}${(cfg.monthlyFeesPerActiveSite ?? 0).toFixed(2)}`)}
            </TableBody>
          </Table>
        </Box>
        <Box className={classes.cfgSection}>
          <Box className={classes.cfgSectionTitle}>HRT Sites</Box>
          <Table size="small" className={classes.cfgTable}>
            <TableBody>
              {cfgRow('Billable', bool(cfg.hrtSitesBillable))}
              {cfgRow('Fee / Site', `${sym}${(cfg.monthlyFeesPerHrtSite ?? 0).toFixed(2)}`)}
            </TableBody>
          </Table>
        </Box>
      </Box>
    </Box>
  )
}

const ProgramWiseInvoiceViewer = (props) => {
  const classes = useStyles()
  const [allData, setAllData] = useState([])
  const [monthKey, setMonthKey] = useState(DEFAULT_MONTH)
  const [loading, setLoading] = useState(false)
  const [open, setOpen] = useState(false)
  const [message, setMessage] = useState('')
  const [messageType, setMessageType] = useState('')
  const [searchText, setSearchText] = useState('')
  const [accountFilter, setAccountFilter] = useState('')
  const [currencyFilter, setCurrencyFilter] = useState('')
  const [programFilter, setProgramFilter] = useState('')
  const [monthFilter, setMonthFilter] = useState('')
  const [sortCol, setSortCol] = useState(null)
  const [sortDir, setSortDir] = useState(-1)
  const [expandedConfig, setExpandedConfig] = useState(null)

  const accountNames = useMemo(() => [...new Set(allData.map(r => r.accountName))].filter(Boolean).sort(), [allData])
  const currencies = useMemo(() => [...new Set(allData.map(r => r.config?.billingCurrency))].filter(Boolean).sort(), [allData])
  const programs = useMemo(() => [...new Set(allData.map(r => r.programName))].filter(Boolean).sort(), [allData])
  const months = useMemo(() => [...new Set(allData.map(r => r.monthKey))].filter(Boolean).sort(), [allData])

  const filteredData = useMemo(() => {
    let rows = allData.filter(r => {
      if (accountFilter && r.accountName !== accountFilter) return false
      if (currencyFilter && (r.config?.billingCurrency ?? '') !== currencyFilter) return false
      if (programFilter && r.programName !== programFilter) return false
      if (monthFilter && r.monthKey !== monthFilter) return false
      if (searchText) {
        const q = searchText.toLowerCase()
        if (
          !(r.accountName ?? '').toLowerCase().includes(q) &&
          !(r.accountId ?? '').toLowerCase().includes(q) &&
          !(r.programName ?? '').toLowerCase().includes(q.replace(/ /g, '_')) &&
          !(r.programName ?? '').toLowerCase().includes(q)
        ) return false
      }
      return true
    })
    if (sortCol) {
      rows = [...rows].sort((a, b) => {
        const av = getSortValue(a, sortCol), bv = getSortValue(b, sortCol)
        return (typeof av === 'string' ? av.localeCompare(bv) : av - bv) * sortDir
      })
    }
    return rows
  }, [allData, accountFilter, currencyFilter, programFilter, monthFilter, searchText, sortCol, sortDir])

  const handleSort = col => {
    if (sortCol === col) {
      setSortDir(d => -d)
    } else {
      setSortCol(col)
      setSortDir(-1)
    }
  }
  
  const fetchData = () => {
    const key = monthKey.trim()
    if (!key) return
    setLoading(true)
    setAllData([])
    eventService.getProgramWiseInvoices(key, props.currentUser?.account_id)
      .then(response => { 
        setAllData(response.data ?? []) 
      })
      .catch(error => {
        setMessage(getErrorMessage(error, FETCH_ERROR_MESSAGE))
        setMessageType(messageTypes.ERROR)
        setOpen(true)
      })
      .finally(() => {
        setLoading(false)
      })
  }

  const footTotals = useMemo(() => {
    const t = {}
    filteredData.forEach(r => {
      const c = r.config?.billingCurrency ?? 'USD'
      if (!t[c]) t[c] = { totalHits: 0, freeHits: 0, overageHits: 0, apiOvg: 0, activeSites: 0, billableSites: 0, sitesFees: 0, hrtSites: 0, hrtFees: 0, fixed: 0, total: 0, taxAmt: 0, totalWithTax: 0, n: 0 }
      t[c].totalHits += r.totalApiHits ?? 0; t[c].freeHits += r.freeHitsAllotted ?? 0
      t[c].overageHits += r.overageApiHits ?? 0; t[c].apiOvg += r.totalApiOverageFees ?? 0
      t[c].activeSites += r.activeSitesCount ?? 0; t[c].billableSites += r.billableSitesCount ?? 0
      t[c].sitesFees += r.totalActiveSitesFees ?? 0; t[c].hrtSites += r.hrtSitesCount ?? 0
      t[c].hrtFees += r.totalHrtSitesFees ?? 0; t[c].fixed += r.fixedFees ?? 0
      t[c].total += r.totalAmount ?? 0; t[c].taxAmt += r.taxAmount ?? 0
      t[c].totalWithTax += r.totalAmountWithTax ?? 0; t[c].n += 1
    })
    return t
  }, [filteredData])

  const statsByCur = useMemo(() => {
    const t = {}
    filteredData.forEach(r => {
      const c = r.config?.billingCurrency ?? 'USD'
      if (!t[c]) t[c] = { total: 0, accounts: new Set() }
      t[c].total += r.totalAmount ?? 0
      t[c].accounts.add(r.accountId)
    })
    return t
  }, [filteredData])

  const uniquePrograms = useMemo(() => new Set(filteredData.map(r => r.programId)).size, [filteredData])
  const totalHits = useMemo(() => filteredData.reduce((s, r) => s + (r.totalApiHits ?? 0), 0), [filteredData])
  const totalSites = useMemo(() => filteredData.reduce((s, r) => s + (r.activeSitesCount ?? 0), 0), [filteredData])

  const exportCsv = () => {
    if (!filteredData.length) return
    const hdr = CSV_HEADERS.map(escapeCsvValue).join(',')
    const body = filteredData.map(r => [
      escapeCsvValue(r.accountName ?? ''), r.accountId ?? '',
      escapeCsvValue(fmtProgName(r.programName ?? '')), r.programId ?? '',
      r.monthKey ?? '', r.config?.billingCurrency ?? '',
      r.totalApiHits ?? 0, r.freeHitsAllotted ?? 0, r.overageApiHits ?? 0,
      (r.totalApiOverageFees ?? 0).toFixed(2),
      r.activeSitesCount ?? 0, r.billableSitesCount ?? 0,
      (r.totalActiveSitesFees ?? 0).toFixed(2),
      r.hrtSitesCount ?? 0, (r.totalHrtSitesFees ?? 0).toFixed(2),
      (r.fixedFees ?? 0).toFixed(2), (r.totalAmount ?? 0).toFixed(2),
      (r.taxAmount ?? 0).toFixed(2), (r.totalAmountWithTax ?? 0).toFixed(2),
      r.status ?? '',
    ].join(','))
    const csv = '\uFEFF' + [hdr, ...body].join('\r\n')
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' })
    const a = document.createElement('a')
    a.href = URL.createObjectURL(blob)
    a.download = `${CSV_FILE_PREFIX}-${new Date().toISOString().slice(0, 10)}.csv`
    a.click()
    URL.revokeObjectURL(a.href)
  }

  const Dash = () => <Box component="span" className={classes.dash}>—</Box>
  const menuProps = { classes: { paper: classes.selectPaper }, anchorOrigin: { vertical: 'bottom', horizontal: 'left' }, getContentAnchorEl: null }

  const sortOrder = col => sortCol === col ? (sortDir === 1 ? SortOrder.ASC : SortOrder.DESC) : SortOrder.ASC

  const tableHeadings = INVOICE_TABLE_COLUMNS.map(col => ({
    ...col,
    sortOrder: col.sortable === false ? null : sortOrder(col.id),
  }))

  const handleSortChange = (col, order) => {
    if (col === 'account' || col === 'config') return
    setSortCol(col)
    setSortDir(order === SortOrder.ASC ? 1 : -1)
  }

  const tableContents = filteredData.map(r => ({ ...r, _isTotal: false }))
  Object.entries(footTotals).sort(([a], [b]) => a.localeCompare(b)).forEach(([cur, t]) => {
    tableContents.push({ id: `__total__${cur}`, _isTotal: true, _currency: cur, ...t })
  })

  const renderCellContent = (item, heading) => {
    const cur = item._isTotal ? item._currency : item.config?.billingCurrency
    if (item._isTotal) {
      if (heading.id === 'account')      
        return `${cur} Total — ${item.n} programs`
      if (heading.id === 'program' || heading.id === 'month' || heading.id === 'currency' || heading.id === 'config') 
        return ''
      if (heading.id === 'totalHits')   
        return fmtNum(item.totalHits)
      if (heading.id === 'freeHits')     
        return fmtNum(item.freeHits)
      if (heading.id === 'overageHits')  
        return fmtNum(item.overageHits)
      if (heading.id === 'apiOvgFees')   
        return item.apiOvg > 0 ? fmtAmt(item.apiOvg, cur) : <Dash />
      if (heading.id === 'activeSites')  
        return item.activeSites > 0 ? item.activeSites.toLocaleString() : <Dash />
      if (heading.id === 'billableSites')
        return item.billableSites > 0 ? item.billableSites.toLocaleString() : <Dash />
      if (heading.id === 'sitesFees')    
        return item.sitesFees > 0 ? fmtAmt(item.sitesFees, cur) : <Dash />
      if (heading.id === 'hrtSites')     
        return item.hrtSites > 0 ? item.hrtSites.toLocaleString() : <Dash />
      if (heading.id === 'hrtFees')      
        return item.hrtFees > 0 ? fmtAmt(item.hrtFees, cur) : <Dash />
      if (heading.id === 'fixedFees')    
        return item.fixed > 0 ? fmtAmt(item.fixed, cur) : <Dash />
      if (heading.id === 'totalAmount')  
        return fmtAmt(item.total, cur)
      if (heading.id === 'taxAmount')    
        return item.taxAmt > 0 ? fmtAmt(item.taxAmt, cur) : <Dash />
      if (heading.id === 'totalWithTax') 
        return item.totalWithTax > 0 ? fmtAmt(item.totalWithTax, cur) : <Dash />
      return ''
    }
    if (heading.id === 'account') {
      return (
        <Box>
          <Box className={classes.acctName}>{item.accountName}</Box>
          <Box className={classes.acctId}>{item.accountId ?? ''}</Box>
        </Box>
      )
    }
    if (heading.id === 'program') {
      return (
        <Box>
          <Box className={classes.progName} title={item.programName}>{fmtProgName(item.programName)}</Box>
          <Box className={classes.progId}>{item.programId ?? ''}</Box>
        </Box>
      )
    }
    if (heading.id === 'month')        return fmtMonth(item.monthKey)
    if (heading.id === 'currency') {
      return (
        <Box component="span" className={`${classes.curBadge} ${cur === 'EUR' ? classes.curEur : classes.curUsd}`}>
          {cur ?? 'USD'}
        </Box>
      )
    }
    if (heading.id === 'totalHits')    
      return fmtNum(item.totalApiHits)
    if (heading.id === 'freeHits')    
      return fmtNum(item.freeHitsAllotted)
    if (heading.id === 'overageHits')  
      return fmtNum(item.overageApiHits)
    if (heading.id === 'apiOvgFees')   
      return item.totalApiOverageFees > 0 ? fmtAmt(item.totalApiOverageFees, cur) : <Dash />
    if (heading.id === 'activeSites')  
      return (item.activeSitesCount ?? 0).toLocaleString()
    if (heading.id === 'billableSites')
      return (item.billableSitesCount ?? 0).toLocaleString()
    if (heading.id === 'sitesFees')    
      return item.totalActiveSitesFees > 0 ? fmtAmt(item.totalActiveSitesFees, cur) : <Dash />
    if (heading.id === 'hrtSites')     
      return (item.hrtSitesCount ?? 0).toLocaleString()
    if (heading.id === 'hrtFees')      
      return item.totalHrtSitesFees > 0 ? fmtAmt(item.totalHrtSitesFees, cur) : <Dash />
    if (heading.id === 'fixedFees')    
      return item.fixedFees > 0 ? fmtAmt(item.fixedFees, cur) : <Dash />
    if (heading.id === 'totalAmount')  
      return fmtAmt(item.totalAmount, cur)
    if (heading.id === 'taxAmount')    
      return item.taxAmount > 0 ? fmtAmt(item.taxAmount, cur) : <Dash />
    if (heading.id === 'totalWithTax') 
      return item.totalAmountWithTax > 0 ? fmtAmt(item.totalAmountWithTax, cur) : <Dash />
    if (heading.id === 'config') {
      return (
        <Button className={classes.configBtn} onClick={() => setExpandedConfig(expandedConfig === item.id ? null : item.id)}>
          ⚙ Config
        </Button>
      )
    }
    return ''
  }

  const getCellClassName = (item, heading) => {
    if (item._isTotal) {
      if (heading.id === 'account')     
        return `${classes.grandTotalCell} ${classes.grandTotalAccountCell}`
      if (heading.id === 'totalAmount' || heading.id === 'totalWithTax') 
        return `${classes.grandTotalCell} ${classes.grandTotalAmountCell}`
      return classes.grandTotalCell
    }
    if (heading.id === 'account')      
      return classes.accountCell
    if (heading.id === 'program')      
      return classes.programCell
    if (heading.id === 'totalAmount' || heading.id === 'totalWithTax') 
      return `${classes.titleWrapper} ${classes.amountCell}`
    return classes.titleWrapper
  }

  const renderExpandedRow = (item, index, colSpan) => {
    if (item._isTotal || expandedConfig !== item.id) return null
    return (
      <TableRow key={`${item.id}-cfg`}>
        <TableCell colSpan={colSpan} className={classes.expandCell}>
          <ConfigPanel r={item} classes={classes} />
        </TableCell>
      </TableRow>
    )
  }

  return (
    <Box>
      <DisplayMessage open={open} message={message} messageType={messageType} handleClose={() => setOpen(false)} />
      <Loader loading={loading} />

      <Box className={classes.urlBar}>
        <Box className={classes.urlRow}>
          <Box className={classes.urlBarLabel}>Month (YYYYMM)</Box>
          <TextField
            className={classes.urlInput}
            value={monthKey}
            onChange={e => setMonthKey(e.target.value)}
            placeholder="e.g. 202609"
            variant="standard"
            InputProps={{ disableUnderline: true }}
            onKeyDown={e => e.key === 'Enter' && fetchData()}
          />
          <Button className={classes.submitBtn} onClick={fetchData} disabled={loading}>
            <Search />
            <Box paddingLeft="8px">Fetch &amp; Render</Box>
          </Button>
        </Box>
      </Box>

      {filteredData.length > 0 && (
        <Box className={classes.statsBar}>
          <Box className={classes.statChip}>
            <Box className={classes.statChipVal}>{filteredData.length}</Box>
            <Box className={classes.statChipLbl}>Program Invoices</Box>
            <Box className={classes.statChipSub}>{accountNames.length} accounts · {uniquePrograms} programs</Box>
          </Box>
          {Object.entries(statsByCur).sort(([a], [b]) => a.localeCompare(b)).map(([cur, s]) => (
            <Box key={cur} className={`${classes.statChip} ${cur === 'EUR' ? classes.statChipBlue : classes.statChipGreen}`}>
              <Box className={classes.statChipVal}>{fmtAmt(s.total, cur)}</Box>
              <Box className={classes.statChipLbl}>Total {cur}</Box>
              <Box className={classes.statChipSub}>{s.accounts.size} account(s)</Box>
            </Box>
          ))}
          <Box className={`${classes.statChip} ${classes.statChipPurple}`}>
            <Box className={classes.statChipVal}>{fmtNum(totalHits)}</Box>
            <Box className={classes.statChipLbl}>Total API Hits</Box>
          </Box>
          <Box className={`${classes.statChip} ${classes.statChipOrange}`}>
            <Box className={classes.statChipVal}>{totalSites.toLocaleString()}</Box>
            <Box className={classes.statChipLbl}>Total Active Sites</Box>
          </Box>
        </Box>
      )}

      <Box className={classes.container}>
        <Box className={classes.card}>
          <Box className={classes.filterBar}>
            <TextField
              className={classes.searchInput}
              value={searchText}
              onChange={e => setSearchText(e.target.value)}
              placeholder="Search account name, ID or program…"
              variant="standard"
              InputProps={{ disableUnderline: true }}
            />
            <Select className={classes.filterSelect} value={accountFilter} onChange={e => setAccountFilter(e.target.value)} displayEmpty disableUnderline MenuProps={menuProps}>
              <MenuItem value="">All Accounts</MenuItem>
              {accountNames.map(n => <MenuItem key={n} value={n}>{n}</MenuItem>)}
            </Select>
            <Select className={classes.filterSelect} value={currencyFilter} onChange={e => setCurrencyFilter(e.target.value)} displayEmpty disableUnderline MenuProps={menuProps}>
              <MenuItem value="">All Currencies</MenuItem>
              {currencies.map(c => <MenuItem key={c} value={c}>{c}</MenuItem>)}
            </Select>
            <Select className={classes.filterSelect} value={programFilter} onChange={e => setProgramFilter(e.target.value)} displayEmpty disableUnderline MenuProps={menuProps}>
              <MenuItem value="">All Programs</MenuItem>
              {programs.map(p => <MenuItem key={p} value={p}>{fmtProgName(p)}</MenuItem>)}
            </Select>
            <Select className={classes.filterSelect} value={monthFilter} onChange={e => setMonthFilter(e.target.value)} displayEmpty disableUnderline MenuProps={menuProps}>
              <MenuItem value="">All Months</MenuItem>
              {months.map(m => <MenuItem key={m} value={m}>{fmtMonth(m)}</MenuItem>)}
            </Select>
            <Box className={classes.filterRight}>
              <Button className={classes.exportBtn} onClick={exportCsv} disabled={!filteredData.length}>
                <Download />
                <Box paddingLeft="5px">Export CSV</Box>
              </Button>
            </Box>
          </Box>

          <TableData
            classes={classes}
            tableHeadings={tableHeadings}
            tableContents={tableContents}
            isBold={sortCol}
            handleSortChange={handleSortChange}
            renderCellContent={renderCellContent}
            getCellClassName={getCellClassName}
            renderExpandedRow={renderExpandedRow}
            containerClassName={classes.tableWrap}
            tableClassName={classes.piTable}
            stickyHeader={false}
            emptyTableText={EMPTY_TABLE_TEXT}
          />
        </Box>
      </Box>
    </Box>
  )
}

const mapStateToProps = ({ auth }) => {
  const { currentUser } = auth
  return { currentUser }
}
export default connect(mapStateToProps)(ProgramWiseInvoiceViewer)
